import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FaRegFileLines } from 'react-icons/fa6';
import { FaPlus } from 'react-icons/fa6';
import Select, { MultiValue } from 'react-select';
import { toast } from 'react-hot-toast';
import {
    createDocument,
    updateDocument,
    uploadDocumentFile,
    DocumentMetadata,
    StandardDocument,
} from '~/services/documents';

interface Props {
    metadata: DocumentMetadata | null;
    // The document being edited, or null when adding a new one.
    document?: StandardDocument | null;
    // Which tab the user is on, so a new document defaults to that type.
    defaultDocType?: string;
    isOpen?: boolean;
    onClose?: () => void;
    onSaved: (doc: StandardDocument) => void;
    // Omitted in edit mode, where the page owns the open/close state.
    showTrigger?: boolean;
    // Called when the modal opens without options loaded, so the page can
    // retry the fetch rather than leave every dropdown empty.
    onRequestMetadata?: () => void;
}

interface Option {
    value: string;
    label: string;
}

// Letters are staff mail-merge templates, never shown in the client materials
// panel, so they don't need a category. Mirrors the backend rule.
const CATEGORY_EXEMPT_DOC_TYPES = ['Letter'];

const EMPTY_FORM = {
    name: '',
    doc_type: 'Form',
    category: '',
    language: 'Any',
    description: '',
    is_active: true,
};

const labelClass = 'mb-1 block text-[12px]/[14px] font-extrabold';
const inputClass =
    'w-full rounded-[4px] border border-[#949599] p-[10px] text-[16px]/[20px]';

const selectClassNames = {
    control: () => 'w-full rounded-[4px] border border-[#949599] text-[16px]/[20px]',
    placeholder: () => 'text-[#949599]',
    multiValue: () => 'bg-[#F4F4F5] rounded-[4px] px-2 py-1 mr-1 mb-1',
    multiValueLabel: () => 'text-[14px]',
    multiValueRemove: () => 'hover:bg-[#F02D3A] hover:text-white rounded-r-[4px] px-1',
};

const AddFileModal: React.FC<Props> = ({
    metadata,
    document: editing = null,
    defaultDocType,
    isOpen,
    onClose,
    onSaved,
    showTrigger = true,
    onRequestMetadata,
}) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const modalOpen = isOpen !== undefined ? isOpen : internalOpen;
    const modal = useRef<HTMLDivElement>(null);

    const [formValues, setFormValues] = useState(EMPTY_FORM);
    const [selectedReasons, setSelectedReasons] = useState<Option[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [dropError, setDropError] = useState<string | null>(null);

    // The extensions the API accepts; anything else is refused there, so
    // catch it here rather than after an upload round trip.
    const onDrop = useCallback((accepted: File[], rejections: any[]) => {
        if (rejections.length > 0) {
            setDropError(
                'That file type is not supported. Use a PDF, Word or Excel '
                + 'document, or a PNG or JPG image.',
            );
            return;
        }
        setDropError(null);
        setFile(accepted[0] || null);
    }, []);

    const { getRootProps, getInputProps, isDragActive, open: openFilePicker } =
        useDropzone({
            onDrop,
            accept: {
                'application/pdf': ['.pdf'],
                'application/msword': ['.doc'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                    ['.docx'],
                'application/vnd.ms-excel': ['.xls'],
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                    ['.xlsx'],
                'image/png': ['.png'],
                'image/jpeg': ['.jpg', '.jpeg'],
            },
            multiple: false,
            noClick: true,
        });
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // A metadata fetch that failed on page load leaves Type, Category and
    // Language with no options at all, which reads as "there are none"
    // rather than "the request failed". Retry each time the form opens.
    useEffect(() => {
        if (modalOpen && !metadata) onRequestMetadata?.();
    }, [modalOpen, metadata, onRequestMetadata]);

    const close = () => {
        if (onClose) onClose();
        else setInternalOpen(false);
    };

    useEffect(() => {
        const keyHandler = ({ key }: KeyboardEvent) => {
            if (modalOpen && key === 'Escape') close();
        };
        window.document.addEventListener('keydown', keyHandler);
        return () => window.document.removeEventListener('keydown', keyHandler);
    }, [modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Seed the form each time the modal opens, from the document being edited
    // or from a clean slate for a new one.
    useEffect(() => {
        if (!modalOpen) return;
        setFieldErrors({});
        setFile(null);
        setDropError(null);
        if (editing) {
            setFormValues({
                name: editing.name || '',
                doc_type: editing.doc_type || 'Form',
                category: editing.category ? String(editing.category.id) : '',
                language: editing.language || 'Any',
                description: editing.description || '',
                is_active: editing.is_active,
            });
            setSelectedReasons(
                (editing.medical_reasons || []).map(r => ({
                    value: String(r.id),
                    label: r.name,
                })),
            );
        } else {
            setFormValues({
                ...EMPTY_FORM,
                doc_type: defaultDocType || EMPTY_FORM.doc_type,
            });
            setSelectedReasons([]);
        }
    }, [modalOpen, editing, defaultDocType]);

    const categoryOptions: Option[] = (metadata?.categories || []).map(c => ({
        value: String(c.id),
        label: c.client_visible ? c.label : `${c.label} (not shown to clients)`,
    }));
    const docTypeOptions: Option[] = (metadata?.doc_types || []).map(t => ({
        value: t.value,
        label: t.label,
    }));
    const languageOptions: Option[] = (metadata?.languages || []).map(l => ({
        value: l.value,
        label: l.label,
    }));
    const reasonOptions: Option[] = (metadata?.medical_reasons || []).map(r => ({
        value: String(r.id),
        label: r.name,
    }));

    const categoryRequired = !CATEGORY_EXEMPT_DOC_TYPES.includes(formValues.doc_type);

    const setValue = (key: keyof typeof EMPTY_FORM, value: any) =>
        setFormValues(prev => ({ ...prev, [key]: value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const errors: Record<string, string> = {};
        if (!formValues.name.trim()) errors.name = 'Name is required.';
        if (categoryRequired && !formValues.category) {
            errors.category =
                'A category is required so the document appears on client dashboards.';
        }
        if (!editing && !file) errors.document = 'Choose a file to upload.';
        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            return;
        }

        setFieldErrors({});
        setSaving(true);
        toast.loading(file ? 'Uploading...' : 'Saving...');

        try {
            // Upload first: if S3 refuses the file, nothing has been written
            // to the document yet.
            let key: string | undefined;
            if (file) key = await uploadDocumentFile(file);

            const payload = {
                name: formValues.name.trim(),
                doc_type: formValues.doc_type,
                category: categoryRequired ? Number(formValues.category) : null,
                medical_reasons: selectedReasons.map(r => Number(r.value)),
                language: formValues.language,
                description: formValues.description.trim(),
                is_active: formValues.is_active,
                ...(key ? { document: key } : {}),
            };

            const saved = editing
                ? await updateDocument(editing.id, payload)
                : await createDocument(payload);

            toast.dismiss();
            toast.success(editing ? 'Document updated.' : 'Document added.');
            onSaved(saved);
            close();
        } catch (err: any) {
            toast.dismiss();
            console.error('Error saving document:', err);
            // Field errors come back keyed by field name; show them inline and
            // keep the modal open so the work isn't lost.
            const data = err?.data;
            if (data && typeof data === 'object') {
                const mapped: Record<string, string> = {};
                Object.entries(data).forEach(([key, value]) => {
                    if (key === 'detail') return;
                    mapped[key] = Array.isArray(value) ? String(value[0]) : String(value);
                });
                if (Object.keys(mapped).length) {
                    setFieldErrors(mapped);
                    toast.error('Please correct the highlighted fields.');
                    return;
                }
            }
            toast.error(err?.message || 'There was an error saving this document');
        } finally {
            setSaving(false);
        }
    };

    const errorText = (field: string) =>
        fieldErrors[field] ? (
            <p className='mt-1 text-[12px] text-red'>{fieldErrors[field]}</p>
        ) : null;

    return (
        <>
            {showTrigger && (
                <button
                    onClick={() => setInternalOpen(true)}
                    className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                >
                    <FaPlus />
                    Add File
                </button>
            )}

            <div
                className={`fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center overflow-y-auto bg-black/90 px-4 py-5 ${
                    modalOpen ? 'block' : 'hidden'
                }`}
            >
                <div
                    ref={modal}
                    className='max-h-full w-full max-w-180 overflow-y-auto rounded-lg bg-white p-6'
                >
                    <div className='flex justify-between'>
                        <h3 className='pb-2 text-xl font-bold sm:text-2xl'>
                            {editing ? 'Edit File' : 'Add File'}
                        </h3>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className='mt-4'>
                            <label htmlFor='doc-name' className={labelClass}>
                                Name
                            </label>
                            <input
                                id='doc-name'
                                type='text'
                                value={formValues.name}
                                onChange={e => setValue('name', e.target.value)}
                                placeholder='Name shown to clients'
                                className={inputClass}
                            />
                            {errorText('name')}
                        </div>

                        <div className='mt-4 flex flex-wrap gap-4'>
                            <div className='min-w-[180px] flex-1'>
                                <label className={labelClass}>Type</label>
                                <Select
                                    menuPlacement='auto'
                                    options={docTypeOptions}
                                    value={
                                        docTypeOptions.find(
                                            o => o.value === formValues.doc_type,
                                        ) || null
                                    }
                                    onChange={option =>
                                        setValue(
                                            'doc_type',
                                            (option as Option | null)?.value || 'Form',
                                        )
                                    }
                                    classNames={selectClassNames}
                                />
                            </div>
                            <div className='min-w-[180px] flex-1'>
                                <label className={labelClass}>
                                    Category{categoryRequired ? '' : ' (not used for letters)'}
                                </label>
                                <Select
                                    menuPlacement='auto'
                                    options={categoryOptions}
                                    isClearable
                                    isDisabled={!categoryRequired}
                                    value={
                                        categoryOptions.find(
                                            o => o.value === formValues.category,
                                        ) || null
                                    }
                                    onChange={option =>
                                        setValue(
                                            'category',
                                            (option as Option | null)?.value || '',
                                        )
                                    }
                                    placeholder='Select category...'
                                    classNames={selectClassNames}
                                />
                                {errorText('category')}
                            </div>
                        </div>

                        <div className='mt-4 flex flex-wrap gap-4'>
                            <div className='min-w-[180px] flex-1'>
                                <label className={labelClass}>Language</label>
                                <Select
                                    menuPlacement='auto'
                                    options={languageOptions}
                                    value={
                                        languageOptions.find(
                                            o => o.value === formValues.language,
                                        ) || null
                                    }
                                    onChange={option =>
                                        setValue(
                                            'language',
                                            (option as Option | null)?.value || 'Any',
                                        )
                                    }
                                    classNames={selectClassNames}
                                />
                            </div>
                            <div className='min-w-[180px] flex-1'>
                                <label className={labelClass}>
                                    Medical Reasons
                                </label>
                                <Select
                                    menuPlacement='auto'
                                    options={reasonOptions}
                                    isMulti
                                    value={selectedReasons}
                                    onChange={(selected: MultiValue<Option>) =>
                                        setSelectedReasons([...selected])
                                    }
                                    placeholder='All medical reasons'
                                    classNames={selectClassNames}
                                />
                            </div>
                        </div>

                        <div className='mt-4'>
                            <label htmlFor='doc-description' className={labelClass}>
                                Description
                            </label>
                            <input
                                id='doc-description'
                                type='text'
                                value={formValues.description}
                                onChange={e => setValue('description', e.target.value)}
                                placeholder='Optional'
                                className={inputClass}
                            />
                        </div>

                        <div className='mt-4'>
                            <label className={labelClass}>File</label>
                            <div
                                {...getRootProps()}
                                className={
                                    'flex flex-col items-center justify-center gap-2 '
                                    + 'rounded-[4px] border-[2px] border-dashed p-6 text-center '
                                    + 'transition-colors duration-200 '
                                    + (isDragActive
                                        ? 'border-primary bg-[#e6f7f8]'
                                        : 'border-[#949599] bg-[#FAFAFA]')
                                }
                            >
                                <input {...getInputProps()} id='doc-file' />
                                {file ? (
                                    <>
                                        <FaRegFileLines className='text-[20px] text-primary' />
                                        <p className='text-[14px]/[18px] font-bold'>
                                            {file.name}
                                        </p>
                                        <button
                                            type='button'
                                            onClick={() => setFile(null)}
                                            className='text-[12px] text-[#F02D3A] underline'
                                        >
                                            Remove
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <p className='text-[14px]/[18px]'>
                                            Drag a file here
                                        </p>
                                        <button
                                            type='button'
                                            onClick={openFilePicker}
                                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                        >
                                            Choose a file
                                        </button>
                                        <p className='text-[12px] text-[#949599]'>
                                            PDF, Word, Excel, PNG or JPG
                                        </p>
                                    </>
                                )}
                            </div>
                            {dropError && (
                                <p className='mt-1 text-[12px] text-[#F02D3A]'>
                                    {dropError}
                                </p>
                            )}
                            {editing?.has_file && !file && (
                                <p className='mt-1 text-[12px] text-gray-600'>
                                    Current file:{' '}
                                    <a
                                        href={editing.url}
                                        target='_blank'
                                        rel='noreferrer'
                                        className='text-primary underline'
                                    >
                                        {editing.file_name}
                                    </a>
                                    . Choosing a new file replaces it.
                                </p>
                            )}
                            {errorText('document')}
                        </div>

                        <div className='mt-4'>
                            <label className='flex cursor-pointer items-center gap-2 text-sm'>
                                <input
                                    type='checkbox'
                                    checked={formValues.is_active}
                                    onChange={e => setValue('is_active', e.target.checked)}
                                    className='rounded border-gray-300 text-primary focus:ring-primary'
                                />
                                <span className='text-gray-700'>
                                    Active (clients and staff can see this document)
                                </span>
                            </label>
                        </div>

                        <div className='mt-6 flex gap-[16px]'>
                            <button
                                type='button'
                                onClick={close}
                                disabled={saving}
                                className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:opacity-50'
                            >
                                Cancel
                            </button>
                            <button
                                type='submit'
                                disabled={saving}
                                className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] disabled:opacity-50'
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AddFileModal;
